import PropTypes from 'prop-types';
import React from 'react';
import Link from 'gatsby-link';
import AnchorLink from 'react-anchor-link-smooth-scroll';
import styled from 'styled-components';

const parseContent = ({ content, ...rest }) =>
  typeof content === 'string' ? (
    <p className="mb-4 leading-normal tracking-normal text-lg" {...rest}>
      {content}
    </p>
  ) : typeof content === 'function' ? (
    content()
  ) : Array.isArray(content) ? (
    content.map((c, i) => parseContent({ content: c, key: i }))
  ) : (
    ''
  );
const CTA = ({ text, to, ...rest }) => {
  const isAnchor = to.indexOf('#') === 0;
  const LinkComponent = isAnchor ? AnchorLink : Link;
  const destination = isAnchor ? { href: to } : { to };
  return (
    <LinkComponent
      offset="100"
      className="bg-primary px-8 py-3 mx-8 text-white tracking-wide no-underline hover:no-underline uppercase block"
      {...destination}
      {...rest}
    >
      {text}
    </LinkComponent>
  );
};

const parseCta = cta =>
  typeof cta === 'function' ? (
    cta
  ) : Array.isArray(cta) ? (
    cta.map((c, i) => <CTA key={i} {...c} />)
  ) : (
    <CTA {...cta} />
  );

const ContentBlockHeader = styled.h2`
  letter-spacing: 6px;
  font-size: 1.5rem;
`;

const ContentBlockCTA = styled.div`
  margin-top: 2rem;
  @media (min-width: 768px) {
    margin-top: 5.2rem;
  }
`;

export const ContentBlock = ({
  title,
  content,
  cta,
  flavor,
  inset,
  maxWidth,
  children,
  ...rest
}) => {
  const backgroundColor =
    flavor === 'dark'
      ? 'bg-accent'
      : flavor === 'light'
        ? 'bg-grey-lighter'
        : 'bg-white';
  const headerColor =
    flavor === 'white'
      ? 'text-grey-darker'
      : flavor === 'dark'
        ? 'text-grey'
        : 'text-grey-darkest';
  const textColor = flavor === 'dark' ? 'text-white' : '';
  return (
    <section
      className={`${backgroundColor} ${textColor} py-16 lg:py-32 px-4`}
      {...rest}
    >
      <ContentBlockHeader
        className={`c-content-block__title mb-8 max-w-xl mx-auto uppercase tracking-wide text-center sm:text-left ${headerColor}`}
      >
        {title}
      </ContentBlockHeader>
      <div
        className={`c-content-block__content max-w-${maxWidth} mx-auto ${
          inset ? 'px-4 xl:px-8' : ''
        } leading-normal tracking-normal text-lg`}
      >
        {content ? parseContent({ content }) : children}
      </div>
      {cta ? (
        <ContentBlockCTA className="c-content-block__cta flex flex-row justify-center">
          {parseCta(cta)}
        </ContentBlockCTA>
      ) : (
        ''
      )}
    </section>
  );
};

const CtaPropTypes = {
  text: PropTypes.string.isRequired,
  to: PropTypes.string.isRequired,
  classes: PropTypes.string,
};

ContentBlock.propTypes = {
  title: PropTypes.string.isRequired,
  content: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.arrayOf(PropTypes.func),
    PropTypes.arrayOf(PropTypes.string),
  ]),
  cta: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.shape(CtaPropTypes)),
    PropTypes.shape(CtaPropTypes),
    PropTypes.func,
  ]),
  flavor: PropTypes.oneOf(['white', 'dark', 'light']),
  inset: PropTypes.bool,
  maxWidth: PropTypes.oneOf(['sm', 'md', 'lg', 'xl', '2xl', '3xl', 'full']),
};

ContentBlock.defaultProps = {
  flavor: 'white',
  inset: true,
  maxWidth: 'xl',
};



// WEBPACK FOOTER //
// ./src/components/ContentBlock.js